import { createContext, useContext, useLayoutEffect, useRef, useState, type MouseEvent, type ReactNode } from 'react'
import { selectionToWindow, type CqGeometry } from './cqZoom'

/**
 * Graficul de timp al ecranului Connection Quality - SVG simplu, fara
 * biblioteca (ca NetworkCharts.tsx). O singura axa Y pe grafic; axa X e
 * timpul adevarat, deci o gaura in masuratori ramane gaura (linia se rupe),
 * nu se lipeste peste ea.
 *
 * Seria poate avea si o banda (min..max), desenata sub linie, transparenta.
 *
 * Sub un `CqChartSync`, graficele ecranului merg impreuna: linia verticala de
 * sub mouse se vede pe TOATE (acelasi moment), iar o tragere pe orizontala
 * peste oricare din ele alege intervalul pentru toate (`onZoom`). Eticheta cu
 * valorile se scrie numai sub graficul peste care e mouse-ul.
 */

type Sync = {
  hover: number | null
  setHover: (t: number | null) => void
  onZoom?: (from: number, to: number) => void
}

const SyncContext = createContext<Sync | null>(null)

export function CqChartSync({ onZoom, children }: { onZoom?: (from: number, to: number) => void; children: ReactNode }) {
  const [hover, setHover] = useState<number | null>(null)
  return <SyncContext.Provider value={{ hover, setHover, onZoom }}>{children}</SyncContext.Provider>
}

/** Punctul seriei cel mai apropiat de `t`, daca e mai aproape de `within` ms. */
export function pointAt(points: CqChartPoint[], t: number, within: number): CqChartPoint | undefined {
  let best: CqChartPoint | undefined
  let dist = Infinity
  for (const p of points) {
    const d = Math.abs(parseTime(p.t) - t)
    if (d < dist) {
      best = p
      dist = d
    }
  }
  return dist <= within ? best : undefined
}

export const CQ_COLORS = ['#2f7ed8', '#d35400', '#7d3c98', '#16a085'] as const

export type CqChartPoint = { t: string; v: number | null; lo?: number | null; hi?: number | null }

export type CqChartSeries = { name: string; color: string; points: CqChartPoint[] }

/**
 * Un moment marcat pe axa timpului (un traseu). `strong` = drumul s-a
 * schimbat: linie rosie si eticheta; celelalte sunt linii discrete.
 */
export type CqChartMarker = { t: string; label: string; strong?: boolean }

/**
 * O linie orizontala de prag (ex. -67 / -72 / -80 dBm pe graficul de semnal
 * Wi-Fi), punctata, cu eticheta la dreapta.
 */
export type CqChartGuide = { v: number; label: string; color?: string }

export function parseTime(s: string): number {
  return new Date(s.replace(' ', 'T')).getTime()
}

/** Pas "rotund" (1, 2, 5 x 10^k) pentru aproximativ `count` diviziuni. */
export function niceStep(range: number, count = 5): number {
  if (!(range > 0)) return 1
  const raw = range / count
  const mag = Math.pow(10, Math.floor(Math.log10(raw)))
  const n = raw / mag
  return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10) * mag
}

/** Bucatile continue ale benzii min..max: [timp, min, max], rupte la gauri. */
function bands(points: CqChartPoint[], gap: number): Array<Array<[number, number, number]>> {
  const out: Array<Array<[number, number, number]>> = []
  let current: Array<[number, number, number]> = []
  let last = -Infinity
  for (const p of points) {
    const t = parseTime(p.t)
    const ok = p.lo !== undefined && p.lo !== null && p.hi !== undefined && p.hi !== null
    if (!ok || t - last > gap) {
      if (current.length) out.push(current)
      current = []
    }
    if (ok) current.push([t, p.lo as number, p.hi as number])
    last = t
  }
  if (current.length) out.push(current)
  return out
}

function two(n: number): string {
  return String(n).padStart(2, '0')
}

function timeLabel(ms: number, spanMs: number): string {
  const d = new Date(ms)
  if (spanMs <= 36 * 3600 * 1000) return `${two(d.getHours())}:${two(d.getMinutes())}`
  return `${two(d.getDate())}.${two(d.getMonth() + 1)}`
}

function fullLabel(ms: number): string {
  const d = new Date(ms)
  return `${two(d.getDate())}.${two(d.getMonth() + 1)} ${two(d.getHours())}:${two(d.getMinutes())}`
}

export function CqChart({
  title,
  unit,
  from,
  to,
  bucketS,
  series,
  yMin = 0,
  yMax,
  minTop = 1,
  height = 240,
  format = (n) => String(Math.round(n * 10) / 10),
  markers = [],
  guides = [],
  actions,
}: {
  title: string
  unit: string
  from: string
  to: string
  bucketS: number
  series: CqChartSeries[]
  yMin?: number
  yMax?: number
  /** Cel mai mic varf al axei cand datele sunt toate zero. */
  minTop?: number
  height?: number
  format?: (n: number) => string
  markers?: CqChartMarker[]
  guides?: CqChartGuide[]
  /** Butoane in capul graficului (Expand pe pagina mare, vederea Grid). */
  actions?: ReactNode
}) {
  const box = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(0)
  const sync = useContext(SyncContext)
  const [ownHover, setOwnHover] = useState<number | null>(null)
  const hover = sync ? sync.hover : ownHover
  const setHover = sync ? sync.setHover : setOwnHover
  /* Graficul peste care e mouse-ul: numai el scrie eticheta cu valori. */
  const [inside, setInside] = useState(false)
  const [drag, setDrag] = useState<{ a: number; b: number } | null>(null)
  const onZoom = sync?.onZoom

  useLayoutEffect(() => {
    const el = box.current
    if (!el) return
    const measure = () => setWidth(el.clientWidth || 600)
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const t0 = parseTime(from)
  const t1 = Math.max(t0 + 60_000, parseTime(to))
  const values = series.flatMap((s) => s.points.flatMap((p) => [p.v, p.hi ?? null])).filter((v): v is number => v !== null)
  const dataMax = values.length ? Math.max(...values) : 0
  const top = yMax ?? Math.max(yMin + minTop, dataMax * 1.1)
  const step = niceStep(top - yMin)
  const yTop = yMax ?? Math.max(step, Math.ceil(top / step) * step)
  const ticks: number[] = []
  for (let v = yMin; v <= yTop + step / 1000; v += step) ticks.push(Math.round(v * 1e6) / 1e6)

  const left = 52
  const right = Math.max(left + 40, width - 12)
  const plotTop = 12
  const plotBottom = height - 26
  const x = (ms: number) => left + ((ms - t0) / (t1 - t0)) * (right - left)
  const y = (v: number) => plotBottom - ((Math.min(Math.max(v, yMin), yTop) - yMin) / (yTop - yMin)) * (plotBottom - plotTop)
  const gap = bucketS * 1000 * 1.5

  const xTicks: number[] = []
  const xStepMs = (t1 - t0) / 6
  for (let i = 0; i <= 6; i++) xTicks.push(t0 + i * xStepMs)

  /* Toate momentele, pentru ata de sub mouse. */
  const times = Array.from(new Set(series.flatMap((s) => s.points.map((p) => parseTime(p.t))))).sort((a, b) => a - b)

  const geometry: CqGeometry = { left, right, t0, t1 }
  const pxOf = (event: MouseEvent<SVGSVGElement>) => event.clientX - event.currentTarget.getBoundingClientRect().left

  const onMove = (event: MouseEvent<SVGSVGElement>) => {
    const px = pxOf(event)
    setInside(true)
    if (drag) setDrag({ a: drag.a, b: Math.min(right, Math.max(left, px)) })
    if (!times.length) return
    let best = times[0]!
    for (const t of times) if (Math.abs(x(t) - px) < Math.abs(x(best) - px)) best = t
    setHover(best)
  }

  const onDown = (event: MouseEvent<SVGSVGElement>) => {
    if (!onZoom || event.button !== 0) return
    event.preventDefault()
    const px = Math.min(right, Math.max(left, pxOf(event)))
    setDrag({ a: px, b: px })
  }

  const onUp = () => {
    if (!drag || !onZoom) return
    const chosen = selectionToWindow(drag.a, drag.b, geometry)
    setDrag(null)
    if (chosen) onZoom(chosen.from, chosen.to)
  }

  const onLeave = () => {
    setInside(false)
    setDrag(null)
    setHover(null)
  }

  /* Pe alt grafic momentul de sub mouse poate sa nu fie un punct al acestuia
     (HTTPS e la 5 minute, restul pe minut): se ia cel mai apropiat, in pasul
     galetii. */
  const at = (s: CqChartSeries) => (hover === null ? undefined : pointAt(s.points, hover, gap))

  const segments = (points: CqChartPoint[], pick: (p: CqChartPoint) => number | null | undefined) => {
    const out: Array<Array<[number, number]>> = []
    let current: Array<[number, number]> = []
    let last = -Infinity
    for (const p of points) {
      const v = pick(p)
      const t = parseTime(p.t)
      if (v === null || v === undefined || t - last > gap) {
        if (current.length) out.push(current)
        current = []
      }
      if (v !== null && v !== undefined) current.push([t, v])
      last = t
    }
    if (current.length) out.push(current)
    return out
  }

  const empty = series.every((s) => s.points.every((p) => p.v === null))

  return (
    <div className="cq-chart">
      <div className="cq-chart-head">
        <h5>{title}</h5>
        {series.length > 1 ? (
          <ul className="cq-legend">
            {series.map((s) => (
              <li key={s.name}>
                <span className="cq-swatch" style={{ background: s.color }}></span>
                {s.name}
              </li>
            ))}
          </ul>
        ) : null}
        {actions ? <div className="cq-chart-actions">{actions}</div> : null}
      </div>
      <div ref={box} style={{ width: '100%', height }}>
        {width > 0 && (
          <svg
            width={width}
            height={height}
            role="img"
            aria-label={`${title}, ${unit}`}
            onMouseMove={onMove}
            onMouseDown={onDown}
            onMouseUp={onUp}
            onMouseLeave={onLeave}
            style={onZoom ? { cursor: 'crosshair', userSelect: 'none' } : undefined}
            data-zoomable={onZoom ? 'yes' : undefined}
          >
            {ticks.map((t) => (
              <g key={t}>
                <line x1={left} x2={right} y1={y(t)} y2={y(t)} stroke="#e6e9ee" strokeWidth={1} />
                <text x={left - 6} y={y(t) + 3} textAnchor="end" className="cq-axis">
                  {format(t)}
                </text>
              </g>
            ))}
            <text x={4} y={plotTop + 2} className="cq-axis" dominantBaseline="hanging">
              {unit}
            </text>
            {guides.map((g) =>
              g.v >= yMin && g.v <= yTop ? (
                <g key={`guide-${g.v}`} data-guide={g.v}>
                  <line
                    x1={left}
                    x2={right}
                    y1={y(g.v)}
                    y2={y(g.v)}
                    stroke={g.color ?? '#95a5a6'}
                    strokeWidth={1}
                    strokeDasharray="5 4"
                  />
                  <text x={right - 2} y={y(g.v) - 3} textAnchor="end" className="cq-guide-label" fill={g.color ?? '#7f8c8d'}>
                    {g.label}
                  </text>
                </g>
              ) : null,
            )}
            {xTicks.map((t, i) => (
              <text key={i} x={x(t)} y={height - 8} textAnchor={i === 0 ? 'start' : i === 6 ? 'end' : 'middle'} className="cq-axis">
                {timeLabel(t, t1 - t0)}
              </text>
            ))}
            {markers.map((m, i) => {
              const ms = parseTime(m.t)
              if (!(ms >= t0 && ms <= t1)) return null
              return (
                <g key={`mark-${i}`} data-marker={m.strong ? 'strong' : 'plain'}>
                  <title>{m.label}</title>
                  <line
                    x1={x(ms)}
                    x2={x(ms)}
                    y1={plotTop}
                    y2={plotBottom}
                    stroke={m.strong ? '#c0392b' : '#b6bfc9'}
                    strokeWidth={m.strong ? 1.5 : 1}
                    strokeDasharray={m.strong ? '4 3' : '1 3'}
                  />
                  {m.strong ? (
                    <text x={x(ms) + 3} y={plotTop + 9} className="cq-mark-label">
                      {m.label}
                    </text>
                  ) : null}
                </g>
              )
            })}
            {series.map((s) =>
              bands(s.points, gap).map((seg, i) => {
                if (seg.length < 2) return null
                const d =
                  seg.map(([t, , hi], k) => `${k === 0 ? 'M' : 'L'}${x(t).toFixed(1)} ${y(hi).toFixed(1)}`).join(' ') +
                  ' ' +
                  [...seg].reverse().map(([t, lo]) => `L${x(t).toFixed(1)} ${y(lo).toFixed(1)}`).join(' ') +
                  ' Z'
                return <path key={`${s.name}-band-${i}`} d={d} fill={s.color} fillOpacity={0.12} stroke="none" data-band={s.name} />
              }),
            )}
            {series.map((s) =>
              segments(s.points, (p) => p.v).map((seg, i) =>
                seg.length === 1 ? (
                  <circle key={`${s.name}-${i}`} cx={x(seg[0]![0])} cy={y(seg[0]![1])} r={2.5} fill={s.color} />
                ) : (
                  <path
                    key={`${s.name}-${i}`}
                    d={seg.map(([t, v], k) => `${k === 0 ? 'M' : 'L'}${x(t).toFixed(1)} ${y(v).toFixed(1)}`).join(' ')}
                    fill="none"
                    stroke={s.color}
                    strokeWidth={2}
                    strokeLinejoin="round"
                    data-series={s.name}
                  />
                ),
              ),
            )}
            {empty ? (
              <text x={(left + right) / 2} y={(plotTop + plotBottom) / 2} textAnchor="middle" className="cq-empty">
                No measurements in this period
              </text>
            ) : null}
            {drag && Math.abs(drag.b - drag.a) > 0 ? (
              <rect
                x={Math.min(drag.a, drag.b)}
                y={plotTop}
                width={Math.abs(drag.b - drag.a)}
                height={plotBottom - plotTop}
                fill="#2f7ed8"
                fillOpacity={0.12}
                stroke="#2f7ed8"
                strokeOpacity={0.5}
                data-selection="yes"
                style={{ pointerEvents: 'none' }}
              />
            ) : null}
            {hover !== null && hover >= t0 && hover <= t1 ? (
              <g style={{ pointerEvents: 'none' }} data-hover-line="yes">
                <line x1={x(hover)} x2={x(hover)} y1={plotTop} y2={plotBottom} stroke="#9aa6b2" strokeDasharray="3 3" />
                {series.map((s) => {
                  const p = at(s)
                  return p && p.v !== null ? (
                    <circle key={s.name} cx={x(parseTime(p.t))} cy={y(p.v)} r={4} fill={s.color} stroke="#fff" strokeWidth={2} />
                  ) : null
                })}
              </g>
            ) : null}
          </svg>
        )}
      </div>
      {hover !== null && (inside || !sync) ? (
        <div className="cq-tip" role="status">
          <strong>{fullLabel(hover)}</strong>
          {series.map((s) => {
            const p = at(s)
            if (!p || p.v === null) return null
            const band = p.lo !== undefined && p.lo !== null && p.hi !== undefined && p.hi !== null ? ` (${format(p.lo)} - ${format(p.hi)})` : ''
            return (
              <span key={s.name}>
                <span className="cq-swatch" style={{ background: s.color }}></span>
                {s.name}: {format(p.v)} {unit}
                {band}
              </span>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}
